import { ArrowRight, Shield, Clock, Star, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";

export const HeroSection = ({ onBookClick }) => {
  const { t } = useLanguage();

  const features = [
    { icon: Shield, text: t.hero.safeRides },
    { icon: Clock, text: t.hero.allDay },
    { icon: Star, text: t.hero.fiveStar },
  ];

  return (
    <section className="hero-section" data-testid="hero-section">
      {/* Background Image - Red Mercedes V */}
      <div className="hero-bg">
        <img
          src="https://taxinora.se/new_assets/img/car/BEY38W.jpg"
          alt="Taxi Kalmar Mercedes V"
        />
        <div className="hero-overlay" />
      </div>

      {/* Content */}
      <div className="section-container relative z-10 pt-32 pb-20 md:pt-40 md:pb-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="text-left">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-yellow-500/10 border border-yellow-500/20 mb-6">
              <span className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" />
              <span className="text-yellow-500 text-sm font-medium">{t.hero.badge}</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight mb-6" data-testid="hero-title">
              {t.hero.title}{" "}
              <span className="text-gradient">{t.hero.titleHighlight}</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-neutral-300 mb-8 max-w-xl leading-relaxed">
              {t.hero.description}
            </p>

            {/* Features */}
            <div className="flex flex-wrap gap-6 mb-10">
              {features.map((feature) => (
                <div key={feature.text} className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                    <feature.icon className="w-5 h-5 text-yellow-500" />
                  </div>
                  <span className="text-white font-medium">{feature.text}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="tel:0480474717">
                <Button className="btn-secondary text-base w-full sm:w-auto" data-testid="hero-call-btn">
                  {t.hero.callUs} 0480 47 47 17
                </Button>
              </a>
            </div>
          </div>

          {/* Right Content - App Download Card */}
          <div className="hidden lg:block">
            <div className="glass-card rounded-2xl p-8 gold-glow">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-14 h-14 bg-yellow-500 rounded-xl flex items-center justify-center">
                  <Smartphone className="w-7 h-7 text-neutral-950" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Vår App</h3>
                  <p className="text-neutral-400 text-sm">{t.hero.downloadApp}</p>
                </div>
              </div>
              
              <p className="text-neutral-300 mb-6">
                {t.app.description}
              </p>

              {/* App Store Links */}
              <div className="flex flex-col gap-3">
                <a
                  href="https://apps.apple.com/se/app/taxi-nora/id6741321616"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="app-btn justify-center"
                  data-testid="hero-app-store-btn"
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  <span className="font-medium">{t.app.appStore}</span>
                </a>
                
                <a
                  href="https://play.google.com/store/apps/details?id=com.taxicaller.TaxiNora.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="app-btn justify-center"
                  data-testid="hero-play-store-btn"
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                  </svg>
                  <span className="font-medium">{t.app.googlePlay}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
